import { getPriceData } from '@/components/product-price/utils';
import { getCartAccessoriesTotal, getMerchandiseProductItems } from '@/lib/cart-accessory-utils';
import { getDisplayShippingTotal } from '@/lib/order-summary-shipping-utils';
import { readAnalyticsCategoryNamesFromSource } from './category-names';
import type { DataLayerEcommerce, DataLayerItem, DataLayerUserData } from './data-layer';
import type { SCAPIShopperBasketsV2Schemas, SCAPIShopperProductsSchemas } from 'src/types';
import type { ShopperBasketsV2, ShopperOrders, ShopperProducts, ShopperSearch } from '@/scapi';
import type { AnalyticsItemList } from '@/providers/analytics-item-list-context';

type UnknownRecord = Record<string, unknown>;

/** GA4 list of the search results grid. */
export const SEARCH_RESULTS_ITEM_LIST = { itemListId: 'search_results', itemListName: 'search results' } as const;
/** GA4 `item_list_name` of the category PLP grid; `item_list_id` is the category id. */
export const CATEGORY_LISTING_ITEM_LIST_NAME = 'main listing';

export type DataLayerItemContext = {
    affiliation?: string;
    coupon?: string;
    itemListId?: string;
    itemListName?: string;
    /**
     * Quantity to report on the emitted GA4 item(s) — e.g. the delta added to cart when it
     * differs from the current basket line quantity. Only set this for single-item events;
     * unit pricing is always de-totalized by the line's own quantity, not this value.
     */
    quantity?: number;
    payment_type?: string;
    shipping_method?: string;
    shipping_tier?: string;
};

export type DataLayerEcommerceContext = DataLayerItemContext & {
    currency?: string;
    value?: number;
    transactionId?: string;
    tax?: number;
    shipping?: number;
    customer_type?: 'new' | 'returning';
    includeValue?: boolean;
};

export type DataLayerCheckoutParameters = {
    payment_type: string;
    shipping_method: string;
    shipping_tier: string;
};

function asRecord(value: unknown): UnknownRecord {
    return value !== null && typeof value === 'object' ? (value as UnknownRecord) : {};
}

/** Product / search-hit fields consumed by {@link mapDataLayerItem}. */
const ANALYTICS_PRODUCT_FIELD_KEYS = [
    'brand',
    'brandName',
    'categoryNames',
    'productType',
    'variationValues',
    'c_analyticsCategoryNames',
    'c_erpProductCode',
    'c_brand',
    'c_brandID',
    'c_pimBrandName',
    'c_productType',
] as const;

/**
 * Copies catalog and search-hit attributes needed for GA4 item mapping.
 * Use when converting Product → SearchHit or building PLP quick-add payloads.
 */
export function pickAnalyticsProductFields(source: unknown): UnknownRecord {
    const record = asRecord(source);
    const picked: UnknownRecord = {};

    for (const key of ANALYTICS_PRODUCT_FIELD_KEYS) {
        if (record[key] !== undefined) {
            picked[key] = record[key];
        }
    }

    return picked;
}

function firstString(...values: unknown[]): string {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) {
            return value;
        }
    }
    return '';
}

function firstNumber(...values: unknown[]): number | undefined {
    for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
        if (typeof value === 'string' && value.trim()) {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }
    }
    return undefined;
}

function firstArray(...values: unknown[]): unknown[] {
    return values.find(Array.isArray) ?? [];
}

function getCoupon(record: UnknownRecord): string {
    const adjustment = asRecord(firstArray(record.priceAdjustments, record.c_priceAdjustments)[0]);
    return firstString(record.coupon, record.couponCode, adjustment.couponCode, adjustment.promotionId);
}

function getVariant(record: UnknownRecord, product: UnknownRecord): string {
    const variationValues = asRecord(record.variationValues ?? product.variationValues);
    const serializedVariationValues = Object.entries(variationValues)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, value]) => `${key}:${String(value)}`)
        .join('|');

    return firstString(
        record.itemVariant,
        record.variant,
        record.variantId,
        product.itemVariant,
        serializedVariationValues
    );
}

function getCategories(record: UnknownRecord, product: UnknownRecord): string[] {
    return readAnalyticsCategoryNamesFromSource(record) ?? readAnalyticsCategoryNamesFromSource(product) ?? [];
}

/**
 * Merges basket/order line-item data with optional catalog product data so GA4
 * mappers can read brand, category, and variant attributes not stored on basket APIs.
 */
export function buildDataLayerItemSource(basketItem: unknown, catalogSource?: unknown): unknown {
    const basketRecord = asRecord(basketItem);
    const catalogRecord = asRecord(catalogSource);

    if (!Object.keys(catalogRecord).length) {
        return basketItem;
    }
    if (!Object.keys(basketRecord).length) {
        return catalogSource;
    }

    return {
        ...catalogRecord,
        ...basketRecord,
        product: catalogRecord,
    };
}

type PriceProduct =
    | ShopperProducts.schemas['Product']
    | (ShopperBasketsV2.schemas['ProductItem'] & Partial<ShopperProducts.schemas['Product']>)
    | (ShopperOrders.schemas['ProductItem'] & Partial<ShopperProducts.schemas['Product']>)
    | ShopperSearch.schemas['ProductSearchHit'];

function isSoldByWeightProduct(record: UnknownRecord, catalog: UnknownRecord): boolean {
    return (
        record.c_soldByWeight === true ||
        catalog.c_soldByWeight === true ||
        record.c_pieceQuantity != null ||
        catalog.c_pieceQuantity != null
    );
}

/**
 * Basket and order lines expose `price` / `priceAfterItemDiscount` as line TOTALS
 * (unit × quantity), matching the same guard the UI uses in {@link getPriceData}
 * (`itemId` + `basePrice`). Catalog products and search hits expose a per-unit `price`.
 */
function isCommerceLineItem(record: UnknownRecord): boolean {
    return 'itemId' in record && 'basePrice' in record;
}

/**
 * Resolves the GA4 unit price so it always matches the price the shopper saw.
 *
 * - Basket/order lines and sold-by-weight products are routed through the exact UI
 *   helper ({@link getPriceData}), which de-totalizes line amounts by dividing by the
 *   line's own quantity. This is the single source of truth shared with the storefront.
 * - Catalog products and search hits already carry a per-unit `price`, so the existing
 *   fallback chain is preserved.
 *
 * The line quantity used for de-totalization is read from the record itself, never from
 * the event quantity (`context.quantity`), so add-to-cart events that report a delta
 * quantity still emit the correct per-unit price.
 */
function resolveDataLayerItemPrice(
    record: UnknownRecord,
    catalog: UnknownRecord,
    context: DataLayerItemContext
): number {
    const quantity = firstNumber(record.quantity, context.quantity) ?? 1;

    if (isCommerceLineItem(record) || isSoldByWeightProduct(record, catalog)) {
        return getPriceData({ ...catalog, ...record } as PriceProduct, { quantity }).currentPrice;
    }

    // Defensive: a line total surfaced without `basePrice` (non-standard payload) must
    // still be reduced to a per-unit price rather than reported as the whole-line amount.
    const lineTotal = firstNumber(record.priceAfterItemDiscount, record.adjustedPrice);
    if (lineTotal !== undefined) {
        const lineQuantity = firstNumber(record.quantity) ?? 1;
        return lineQuantity > 0 ? lineTotal / lineQuantity : lineTotal;
    }

    return (
        firstNumber(
            record.price,
            catalog.price,
            record.minPrice,
            catalog.minPrice,
            record.c_piecePrice,
            catalog.c_piecePrice,
            record.c_pieceUnitPrice,
            catalog.c_pieceUnitPrice
        ) ?? 0
    );
}

function resolveDataLayerItemBasePrice(record: UnknownRecord, catalog: UnknownRecord): number | undefined {
    if (isSoldByWeightProduct(record, catalog)) {
        return firstNumber(record.c_pieceListPrice, catalog.c_pieceListPrice);
    }

    return firstNumber(record.basePrice, record.priceBeforeItemDiscount, catalog.basePrice, catalog.price);
}

/**
 * Normalizes Product, ProductSearchHit, ProductItem, and order ProductItem
 * shapes into the single GA4 item contract used by all Eataly commerce events.
 */
export function mapDataLayerItem(source: unknown, index = 0, context: DataLayerItemContext = {}): DataLayerItem {
    const record = asRecord(source);
    const catalog = { ...asRecord(record.representedProduct), ...asRecord(record.product) };
    const categories = getCategories(record, catalog);
    const price = resolveDataLayerItemPrice(record, catalog, context);
    const basePrice = resolveDataLayerItemBasePrice(record, catalog);
    const affiliation = firstString(record.affiliation, catalog.affiliation, context.affiliation);
    const coupon = getCoupon(record) || getCoupon(catalog) || context.coupon || '';
    const itemBrand = firstString(
        record.brand,
        record.brandName,
        record.c_brand,
        record.c_brandID,
        record.c_pimBrandName,
        catalog.brand,
        catalog.brandName,
        catalog.c_brand,
        catalog.c_brandID,
        catalog.c_pimBrandName
    );
    const itemListId = firstString(record.itemListId, record.item_list_id, context.itemListId);
    const itemListName = firstString(record.itemListName, record.item_list_name, context.itemListName);
    const itemVariant = getVariant(record, catalog);

    return {
        // GA4 receives the raw ERP code; the catalog id carries the site productIdPrefix.
        item_id: firstString(
            record.c_erpProductCode,
            catalog.c_erpProductCode,
            record.productId,
            record.id,
            catalog.id,
            catalog.productId
        ),
        item_name: firstString(record.productName, record.itemText, record.name, catalog.name, catalog.productName),
        ...(affiliation ? { affiliation } : {}),
        ...(coupon ? { coupon } : {}),
        discount: basePrice === undefined ? 0 : Math.max(0, basePrice - price),
        index: firstNumber(record.index) ?? index,
        ...(itemBrand ? { item_brand: itemBrand } : {}),
        ...(categories[0] ? { item_category: categories[0] } : {}),
        ...(categories[1] ? { item_category2: categories[1] } : {}),
        ...(categories[2] ? { item_category3: categories[2] } : {}),
        ...(categories[3] ? { item_category4: categories[3] } : {}),
        ...(categories[4] ? { item_category5: categories[4] } : {}),
        ...(itemListId ? { item_list_id: itemListId } : {}),
        ...(itemListName ? { item_list_name: itemListName } : {}),
        ...(itemVariant ? { item_variant: itemVariant } : {}),
        price,
        // Emit the event quantity when provided (e.g. the delta added to cart), falling
        // back to the line's own quantity. Pricing above always de-totalizes by the line
        // quantity, so a delta event still reports the correct per-unit price and value.
        quantity: firstNumber(context.quantity, record.quantity) ?? 1,
    };
}

/**
 * GA4 list attribution for events fired from a tile inside a tracked list (quick-add, quantity
 * stepper, wishlist). Returns the sources unchanged and an empty context outside a list.
 */
export function getItemListTracking(
    sources: readonly unknown[],
    itemList: AnalyticsItemList | null | undefined
): { items: readonly unknown[]; context: Pick<DataLayerItemContext, 'itemListId' | 'itemListName'> } {
    if (!itemList) {
        return { items: sources, context: {} };
    }
    return {
        items:
            itemList.index === undefined
                ? sources
                : sources.map((source) => ({ ...asRecord(source), index: itemList.index })),
        context: { itemListId: itemList.itemListId, itemListName: itemList.itemListName },
    };
}

export function getCommerceItems(
    source: SCAPIShopperBasketsV2Schemas['Basket'] | undefined
): SCAPIShopperBasketsV2Schemas['ProductItem'][] {
    const record = asRecord(source);
    return firstArray(record.productItems, record.items) as SCAPIShopperBasketsV2Schemas['ProductItem'][];
}

export function buildDataLayerEcommerce(
    sources: readonly unknown[],
    context: DataLayerEcommerceContext = {}
): DataLayerEcommerce {
    const items = sources.map((source, index) => mapDataLayerItem(source, index, context));
    const calculatedValue = items.reduce((total, item) => total + item.price * item.quantity, 0);

    return {
        ...(context.currency ? { currency: context.currency } : {}),
        ...(context.payment_type ? { payment_type: context.payment_type } : {}),
        ...(context.shipping_method ? { shipping_method: context.shipping_method } : {}),
        ...(context.shipping_tier ? { shipping_tier: context.shipping_tier } : {}),
        ...(context.itemListId ? { item_list_id: context.itemListId } : {}),
        ...(context.itemListName ? { item_list_name: context.itemListName } : {}),
        ...(context.includeValue === false ? {} : { value: context.value ?? calculatedValue }),
        ...(context.transactionId ? { transaction_id: context.transactionId } : {}),
        ...(context.tax !== undefined ? { tax: context.tax } : {}),
        ...(context.shipping !== undefined ? { shipping: context.shipping } : {}),
        ...(context.customer_type ? { customer_type: context.customer_type } : {}),
        items,
    };
}

export function getBasketEcommerceContext(
    basket: unknown,
    overrides: DataLayerEcommerceContext = {}
): DataLayerEcommerceContext {
    const record = asRecord(basket);
    const couponItem = asRecord(firstArray(record.couponItems)[0]);

    return {
        currency: firstString(overrides.currency, record.currency),
        value: overrides.value ?? firstNumber(record.productTotal),
        coupon: firstString(overrides.coupon, couponItem.code, couponItem.couponCode),
        transactionId: firstString(overrides.transactionId, record.orderNo, record.orderNumber),
        tax: overrides.tax ?? firstNumber(record.taxTotal, record.totalTax),
        shipping:
            overrides.shipping ??
            getDisplayShippingTotal(basket as SCAPIShopperBasketsV2Schemas['Basket']) ??
            firstNumber(record.shippingTotal, record.shippingTotalPrice, record.adjustedShippingTotalPrice),
        affiliation: overrides.affiliation,
        itemListId: overrides.itemListId,
        itemListName: overrides.itemListName,
        payment_type: overrides.payment_type,
        shipping_method: overrides.shipping_method,
        shipping_tier: overrides.shipping_tier,
    };
}

function normalizeShippingMethod(...values: unknown[]): string {
    const shippingMethod = firstString(...values).toLowerCase();
    if (!shippingMethod) {
        return '';
    }
    return /(pickup|pick-up|collect|ritiro)/.test(shippingMethod) ? 'pickup point' : 'delivery';
}

function normalizeShippingTier(...values: unknown[]): string {
    const shippingTier = firstString(...values).toLowerCase();
    if (shippingTier.includes('express')) {
        return 'express';
    }
    if (shippingTier.includes('ground')) {
        return 'ground';
    }
    return shippingTier ? 'standard' : '';
}

function normalizePaymentType(paymentInstrument: UnknownRecord): string {
    const paymentType = firstString(paymentInstrument.paymentMethodId, paymentInstrument.paymentMethod).toLowerCase();
    if (paymentType.includes('card') || Object.keys(asRecord(paymentInstrument.paymentCard)).length > 0) {
        return 'credit card';
    }
    return paymentType.replaceAll('_', ' ');
}

export function getBasketCheckoutParameters(basket: unknown): DataLayerCheckoutParameters {
    const record = asRecord(basket);
    const shipment = asRecord(firstArray(record.shipments)[0]);
    const shippingMethod = asRecord(shipment.shippingMethod);
    const paymentInstrument = asRecord(firstArray(record.paymentInstruments)[0]);

    return {
        payment_type: normalizePaymentType(paymentInstrument),
        shipping_method: normalizeShippingMethod(shippingMethod.id, shippingMethod.name),
        shipping_tier: normalizeShippingTier(shippingMethod.id, shippingMethod.name),
    };
}

export function getPurchaseCategory(sources: readonly unknown[]): 'product' | 'class' | 'event' | 'mixed' {
    const categories = new Set<'product' | 'class' | 'event'>();

    for (const source of sources) {
        const record = asRecord(source);
        const product = asRecord(record.product);
        const productType = firstString(
            record.c_productType,
            record.productType,
            product.c_productType,
            product.productType
        ).toLowerCase();

        if (/(course|class|corsi)/.test(productType)) {
            categories.add('class');
        } else if (/(event|eventi)/.test(productType)) {
            categories.add('event');
        } else {
            categories.add('product');
        }
    }

    return categories.size > 1 ? 'mixed' : (categories.values().next().value ?? 'product');
}

export function buildBasketEcommerce(
    basket: SCAPIShopperBasketsV2Schemas['Basket'],
    overrides: DataLayerEcommerceContext = {}
): DataLayerEcommerce {
    return buildDataLayerEcommerce(getCommerceItems(basket), getBasketEcommerceContext(basket, overrides));
}

/**
 * Maps basket line items enriched with catalog product data for GA4 basket/checkout events.
 */
export function buildEnrichedBasketEcommerce(
    basket: SCAPIShopperBasketsV2Schemas['Basket'],
    productsByItemId: Record<string, SCAPIShopperProductsSchemas['Product']> = {},
    overrides: DataLayerEcommerceContext = {}
): DataLayerEcommerce {
    const items = getCommerceItems(basket);
    const enrichedItems = getMerchandiseProductItems(items).map((item) => {
        const itemRecord = asRecord(item);
        const itemId = firstString(itemRecord.itemId);

        return buildDataLayerItemSource(item, itemId ? productsByItemId[itemId] : undefined);
    });
    const productTotal = firstNumber(basket.productTotal);

    return buildDataLayerEcommerce(
        enrichedItems,
        getBasketEcommerceContext(basket, {
            ...(productTotal === undefined
                ? {}
                : { value: Math.round((productTotal - getCartAccessoriesTotal(items)) * 100) / 100 }),
            ...overrides,
        })
    );
}

export function buildDataLayerUserData(
    source: SCAPIShopperBasketsV2Schemas['Basket'],
    overrides?: Pick<DataLayerUserData, 'email_address'>
): DataLayerUserData {
    const record = asRecord(source);
    const shipment = asRecord(firstArray(record.shipments)[0]);
    const shippingAddress = asRecord(shipment.shippingAddress);
    const billingAddress = asRecord(record.billingAddress);
    const customerInfo = asRecord(record.customerInfo);
    const address = Object.keys(shippingAddress).length > 0 ? shippingAddress : billingAddress;

    return {
        first_name: firstString(address.firstName, customerInfo.firstName, record.firstName),
        last_name: firstString(address.lastName, customerInfo.lastName, record.lastName),
        email_address: firstString(customerInfo.email, record.customerEmail, record.email, overrides?.email_address),
        phone_number: firstString(address.phone, customerInfo.phone, record.phone),
        street: firstString(address.address1, address.address2),
        city: firstString(address.city),
        region: firstString(address.stateCode, address.state),
        country: firstString(address.countryCode, address.country),
        zip_code: firstString(address.postalCode, address.zipCode),
    };
}
